import { useTiclickTableContext } from '../context/ticlick-table-context-hook';
import { useSearchParams } from 'react-router-dom';
import type { TView } from '@src/shared/types/view';
import type { TTiclickTableFilter } from './use-table-filters';
import type { OnChangeFn } from '@tanstack/react-table';

export const useTableStates = () => {
  const { table } = useTiclickTableContext();
  const [, setSp] = useSearchParams();

  const {
    setGlobalFilter,
    setColumnOrder,
    setColumnPinning,
    setColumnVisibility,
    setColumnSizing,
    setPagination,
  } = table;

  const {
    globalFilter,
    columnOrder,
    columnPinning,
    columnVisibility,
    columnSizing,
    pagination,
  } = table.getState();

  const { setColumnNames, columnNames, setCurrentView, currentView } =
    table.options.meta?.state || {};

  const initialSettings = table.options.meta?.initialSettings;

  const setCurrentViewAndTableSettings = (view?: TView) => {
    setCurrentView?.(view);

    setGlobalFilter({
      staticFilters: view?.static_filters,
      customFieldFilters: view?.custom_filters,
      dynamicFilters: view?.filters,
    });

    const viewOrdering = view?.visible_static_fields?.ordering || [];
    const viewVisibility = view?.visible_static_fields?.visibility || {};

    setColumnOrder(
      viewOrdering.length ? viewOrdering : initialSettings?.ordering || []
    );
    setColumnPinning(view?.visible_static_fields?.pinning || {});
    setColumnVisibility(
      Object.keys(viewVisibility).length
        ? viewVisibility
        : initialSettings?.visibles || {}
    );
    setColumnSizing(view?.visible_static_fields?.sizing || {});
    setColumnNames?.(view?.visible_static_fields?.names);

    setSp((prev) => {
      if (view?.id) {
        return { ...prev, view: view.id };
      }
      prev.delete('view');
      return prev;
    });
  };

  return {
    tableFilters: globalFilter,
    setTableFilters: setGlobalFilter as OnChangeFn<TTiclickTableFilter>,

    columnOrder,
    setColumnOrder,

    columnPinning,
    setColumnPinning,

    columnVisibility,
    setColumnVisibility,

    columnSizing,
    setColumnSizing,

    columnNames,
    setColumnNames,

    pagination,
    setPagination,

    currentView,
    setCurrentView: setCurrentViewAndTableSettings,
  };
};
